import { useState, useEffect } from "react";
import { EXPENSE_CATEGORIES } from "../data/subsidies";
import { useApi } from "../hooks/useApi";

const today = () => new Date().toISOString().slice(0, 10);
const yen = (n) => n.toLocaleString("ja-JP") + "円";

const LEDGER_KEY = "nouen_ledger_entries";

const INITIAL_ENTRIES = [
  { id: 1, date: "2026-04-01", type: "income",  amount: 280000, cat: "売上",     memo: "米の出荷" },
  { id: 2, date: "2026-04-05", type: "expense", amount: 12000,  cat: "農薬・肥料", memo: "肥料購入" },
  { id: 3, date: "2026-04-10", type: "expense", amount: 45000,  cat: "燃料費",   memo: "軽油" },
];

function loadEntries() {
  try {
    const saved = JSON.parse(localStorage.getItem(LEDGER_KEY));
    if (Array.isArray(saved)) return saved;
  } catch { /* 破損データは初期値で上書き */ }
  return INITIAL_ENTRIES;
}

export default function LedgerScreen() {
  const [entries,   setEntries]   = useState(loadEntries);

  useEffect(() => {
    localStorage.setItem(LEDGER_KEY, JSON.stringify(entries));
  }, [entries]);
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState({ date: today(), type: "expense", amount: "", cat: EXPENSE_CATEGORIES[0], memo: "" });
  const [aiTip,     setAiTip]     = useState("");
  const { call, loading } = useApi();

  const income  = entries.filter((e) => e.type === "income").reduce((s, e) => s + e.amount, 0);
  const expense = entries.filter((e) => e.type === "expense").reduce((s, e) => s + e.amount, 0);
  const profit  = income - expense;

  const addEntry = () => {
    if (!form.amount) return;
    setEntries([...entries, { ...form, id: Date.now(), amount: Number(form.amount) }]);
    setForm({ date: today(), type: "expense", amount: "", cat: EXPENSE_CATEGORIES[0], memo: "" });
    setShowForm(false);
  };

  const getAiTip = async () => {
    const tip = await call({
      messages: [{
        role: "user",
        content: `農家の今月の収支データです。
収入：${yen(income)}、支出：${yen(expense)}、収支：${yen(profit)}
支出内訳：${entries.filter((e) => e.type === "expense").map((e) => `${e.cat}${yen(e.amount)}`).join("、")}
この内容を見て、補助金活用や経費管理の観点から2〜3点、短くアドバイスしてください。税務の個別判断は避け、一般的なアドバイスのみ。`,
      }],
    });
    setAiTip(tip);
  };

  return (
    <div className="screen">
      <h2 className="screen-title">📒 かんたん帳簿</h2>

      {/* サマリー */}
      <div className="summary-grid">
        <div className="summary-card" style={{ background: "#e8f5e9" }}>
          <div className="summary-label">💰 収入</div>
          <div className="summary-val" style={{ color: "#2e7d32" }}>{yen(income)}</div>
        </div>
        <div className="summary-card" style={{ background: "#fce4ec" }}>
          <div className="summary-label">💸 支出</div>
          <div className="summary-val" style={{ color: "#c62828" }}>{yen(expense)}</div>
        </div>
        <div className="summary-card" style={{ background: profit >= 0 ? "#e3f2fd" : "#fff3e0", gridColumn: "1/-1" }}>
          <div className="summary-label">📊 収支</div>
          <div className="summary-val" style={{ color: profit >= 0 ? "#1565c0" : "#e65100" }}>{yen(profit)}</div>
        </div>
      </div>

      {/* AI分析 */}
      <button className="ai-tip-btn" onClick={getAiTip} disabled={loading}>
        {loading ? "分析中..." : "🤖 AIに経営アドバイスを聞く"}
      </button>
      {aiTip && (
        <div className="ai-tip-box">
          <div className="ai-tip-header">🌾 農援AIからのアドバイス</div>
          <p className="ai-tip-text">{aiTip}</p>
        </div>
      )}

      {/* 明細ヘッダー */}
      <div className="ledger-header">
        <span className="ledger-title">明細一覧</span>
        <button className="add-btn" onClick={() => setShowForm(!showForm)}>＋ 追加</button>
      </div>

      {/* 入力フォーム */}
      {showForm && (
        <div className="form-box">
          <div className="form-row">
            <label className="form-label">日付</label>
            <input type="date" className="form-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="form-row">
            <label className="form-label">種別</label>
            <div className="type-toggle">
              {["income", "expense"].map((t) => (
                <button key={t} className={`type-btn ${form.type === t ? "active" : ""}`} onClick={() => setForm({ ...form, type: t })}>
                  {t === "income" ? "💰 収入" : "💸 支出"}
                </button>
              ))}
            </div>
          </div>
          <div className="form-row">
            <label className="form-label">金額</label>
            <input type="number" className="form-input" placeholder="例：12000" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          {form.type === "expense" && (
            <div className="form-row">
              <label className="form-label">カテゴリ</label>
              <select className="form-input" value={form.cat} onChange={(e) => setForm({ ...form, cat: e.target.value })}>
                {EXPENSE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div className="form-row">
            <label className="form-label">メモ</label>
            <input className="form-input" placeholder="例：肥料購入" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
          </div>
          <button className="primary-btn" onClick={addEntry}>登録する</button>
        </div>
      )}

      {/* 明細リスト */}
      <div className="entries-list">
        {[...entries].reverse().map((e) => (
          <div key={e.id} className="entry-row">
            <div className="entry-left">
              <div className="entry-date">{e.date}</div>
              <div className="entry-memo">
                {e.memo}
                {e.cat && <span className="entry-cat">{e.cat}</span>}
              </div>
            </div>
            <div className="entry-amount" style={{ color: e.type === "income" ? "#2e7d32" : "#c62828" }}>
              {e.type === "income" ? "+" : "-"}{yen(e.amount)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
